import React, { useState, useRef, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styles from './MobileMenu.scss';
import MobileMenuItem from '../mobile-menu-item/MobileMenuItem';
import SubMenu from './SubMenu';
import formatTestId from '../../../utils/test-id/format-testid';

import EnvVarsFeaturesContext from '../../../scaffold/EnvVarsFeaturesContext';
import { getStaticMobileMenus } from '../../links';
import { UserContext } from '../../../scaffold/UserContext';
import FeedbackMenu from './FeedbackMenu';
import isMonetateGroup from '../../../utils/monetate/isMonetateGroup';

// Currently ATG will not return top level categories that have isDisplayedInGlobalNav=false
// Inspiration is a category that we want to display on Mobile and not Desktop so we manually add it to the end here
// Once ATG goes away, hopefully so can this.
const InspirationLink = {
  name: 'Inspiration',
  url: '/content',
  id: '10139580012',
};

const MobileMenu = ({ menus: initialMenus = [], fetchChildCategories }) => {
  const rootMenuName = initialMenus[0]?.name || '';
  const [rootMenuItems, setRootMenuItems] = useState([
    ...(initialMenus[0]?.child || []),
    InspirationLink,
  ]);
  const top = useRef();
  const [activeLevel, setActiveLevel] = useState(0);
  const {
    userState: {
      user: { signedIn },
    },
  } = useContext(UserContext);

  const {
    features: { isBrandPromiseActive, monetateExperiences },
    envVars: { accountBaseUrl },
  } = useContext(EnvVarsFeaturesContext);

  const isDeliveryThresholdActive = isMonetateGroup(
    { monetateExperiences },
    'delivery-threshold'
  );

  const staticMenus = getStaticMobileMenus(accountBaseUrl, signedIn);

  const scrollToTop = () => {
    if (top.current && top.current.scrollBy) {
      top.current.scrollBy(0, -1000);
    }
  };

  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const fetchSubMenuData = async (menuItemId, menuIndex) => {
    if (rootMenuItems[menuIndex].isCached) {
      return;
    }

    try {
      const fetchedData = await fetchChildCategories({
        id: menuItemId,
      });

      if (mounted.current === false) {
        return;
      }

      if (!fetchedData || fetchedData.error) {
        throw new Error('Fetch failed');
      }

      setRootMenuItems(existingRootMenuItems =>
        existingRootMenuItems.map((existing, i) =>
          i === menuIndex
            ? {
                ...existing,
                child: fetchedData.child || [],
                isCached: true,
              }
            : existing
        )
      );
    } catch (e) {
      console.error(`fetch failed for category id: ${menuItemId}`);
    }
  };

  const activeMenuStyles = [
    styles.activeLevelRoot,
    styles.activeLevelFirst,
    styles.activeLevelSecond,
    styles.activeLevelThird,
  ];
  const subMenuClassName = classNames([
    styles.subMenu,
    activeMenuStyles[activeLevel],
  ]);

  return (
    <>
      <nav
        className={classNames(styles.mobileMenu, {
          [styles.mobileMenu__bp]: isBrandPromiseActive,
          [styles.mobileMenu__threshold]: isDeliveryThresholdActive,
        })}
        ref={top}
        data-testid="mobile-navigation-menu"
      >
        <div
          className={subMenuClassName}
          data-testid="mobile-navigation-container"
        >
          <SubMenu
            level={0}
            setActiveLevel={setActiveLevel}
            subMenuData={{
              name: rootMenuName,
              child: rootMenuItems,
              backLabel: undefined,
            }}
            onItemSelected={(menuItemId, menuIndex) => {
              fetchSubMenuData(menuItemId, menuIndex);
            }}
            scrollToTop={scrollToTop}
          >
            {staticMenus.map(menu =>
              menu.feedback ? (
                <FeedbackMenu
                  key={menu.name}
                  menu={menu}
                  isBrandPromiseActive={isBrandPromiseActive}
                />
              ) : (
                <React.Fragment key={menu.name}>
                  <h3 className={classNames(styles.mobileMenu__name)}>
                    {menu.url ? (
                      <a
                        href={menu.url}
                        data-testid={`${formatTestId(
                          menu.name,
                          'mobile-navigation-links-'
                        )}`}
                      >
                        {menu.name}
                      </a>
                    ) : (
                      menu.name
                    )}
                  </h3>

                  {menu.child && (
                    <ul className={styles.mobileMenuList}>
                      {menu.child.map(menuItem => {
                        const { name, url } = menuItem;
                        return (
                          <MobileMenuItem
                            key={name}
                            label={name}
                            url={url}
                            isBrandPromiseActive={isBrandPromiseActive}
                          />
                        );
                      })}
                    </ul>
                  )}
                </React.Fragment>
              )
            )}
          </SubMenu>
        </div>
      </nav>
    </>
  );
};

const itemShape = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  url: PropTypes.string,
};
itemShape.child = PropTypes.arrayOf(PropTypes.shape(itemShape));

MobileMenu.propTypes = {
  menus: PropTypes.arrayOf(PropTypes.shape(itemShape)).isRequired,
  fetchChildCategories: PropTypes.func.isRequired,
};

export default MobileMenu;
